import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, ArrowLeft, Home, MapPin, IndianRupee } from "lucide-react";

const CITIES = [
  "Mumbai", "New Delhi", "Bengaluru", "Hyderabad", "Chennai", "Kolkata", "Pune",
  "Ahmedabad", "Jaipur", "Lucknow", "Kanpur", "Nagpur", "Indore", "Bhopal",
  "Visakhapatnam", "Patna", "Vadodara", "Ludhiana", "Agra", "Nashik",
  "Faridabad", "Meerut", "Rajkot", "Varanasi", "Amritsar", "Allahabad (Prayagraj)",
  "Ranchi", "Coimbatore", "Gwalior", "Vijayawada", "Jodhpur", "Madurai", "Raipur",
  "Guwahati", "Chandigarh", "Mysuru", "Tiruchirappalli", "Gurugram", "Noida",
  "Ghaziabad", "Thiruvananthapuram", "Kochi", "Bhubaneswar", "Dehradun",
  "Navi Mumbai", "Thane", "Surat", "Srinagar", "Jammu", "Goa (Panaji)",
  "Pondicherry", "Howrah", "Other"
];

const HOME_TEST_PRICE = 3500;

export default function Book() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const submissionId = searchParams.get("submissionId") || "";
  const preselectDoctor = searchParams.get("doctor") || "";

  // Steps: 1 = pick city, 2 = pick doctor, 3 = details + payment
  const [step, setStep] = useState(preselectDoctor ? 3 : 1);
  const [city, setCity] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState(preselectDoctor);
  const [patient, setPatient] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [dateVal, setDateVal] = useState("");
  const [consent, setConsent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Load doctors when city changes (step 2 onwards)
  useEffect(() => {
    if (preselectDoctor && !doctors.length) {
      api.get("/doctors").then(r => {
        setDoctors(r.data.doctors);
        const d = r.data.doctors.find(x => x.id === preselectDoctor);
        if (d) setCity(d.city);
      });
      return;
    }
    if (!city) return;
    const params = { city };
    api.get("/doctors", { params }).then(r => setDoctors(r.data.doctors)).catch(() => setDoctors([]));
  }, [city, preselectDoctor, doctors.length]);

  const selectedDoctor = doctors.find(d => d.id === doctorId);

  const submitBooking = async () => {
    if (!patient.name || !patient.email || !patient.phone || !patient.address || !doctorId || !dateVal) {
      setErr("Please fill in name, email, phone, home address, doctor and preferred date.");
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      const { data } = await api.post("/bookings", {
        patient_name: patient.name,
        patient_email: patient.email,
        patient_phone: patient.phone,
        patient_city: selectedDoctor?.city || city || "Other",
        patient_address: patient.address,
        doctor_id: doctorId,
        preferred_date: dateVal,
        notes: patient.notes,
        quiz_submission_id: submissionId || null,
        consent,
      });
      nav(`/thank-you?id=${data.booking_id}&price=${data.price_inr}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="book-page">
      <Nav theme="light" />
      <div className="sv-book">
        <div className="sv-book-inner">
          <Link to="/" style={{ fontSize: 13, color: "var(--ink-60)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}>← Home</Link>

          {/* Home sleep test explainer — always visible at top */}
          <div style={{ background: "white", border: "var(--border)", borderRadius: "var(--radius-lg)", padding: "24px 28px", marginTop: 24, marginBottom: 28, display: "flex", gap: 16, alignItems: "flex-start" }} data-testid="home-test-card">
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(15,110,86,0.1)", color: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Home size={20} />
            </div>
            <div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 500, color: "var(--navy)", marginBottom: 4 }}>Home Sleep Study · ₹{HOME_TEST_PRICE.toLocaleString()}</div>
              <p style={{ fontSize: 14, color: "var(--grey-text)", lineHeight: 1.55, margin: 0 }}>
                A certified technician comes to your home on the agreed date. They set up a small monitor before you go to sleep and collect it the next morning. Results are reviewed by your chosen specialist within 48 hours.
              </p>
            </div>
          </div>

          <div className="sv-book-step-label">Step {step} of 3</div>

          {step === 1 && (
            <>
              <h1 data-testid="book-step-1-headline">Which city are you in?</h1>
              <p style={{ fontSize: 15, color: "var(--ink-60)", margin: "12px 0 24px" }}>
                We&apos;ll only show you specialists who have a verified sleep-study technician team serving your city.
              </p>
              <label className="sv-label">Your city</label>
              <select
                value={city}
                onChange={e => { setCity(e.target.value); setDoctorId(""); }}
                className="sv-input"
                style={{ marginBottom: 20 }}
                data-testid="book-city"
              >
                <option value="">Select your city</option>
                {CITIES.map(c => <option key={c}>{c}</option>)}
              </select>
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button
                  onClick={() => setStep(2)}
                  disabled={!city}
                  className="btn btn-dark"
                  data-testid="book-next-1"
                >
                  See available specialists <ArrowRight size={14} />
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h1 data-testid="book-step-2-headline">Pick your sleep specialist.</h1>
              <p style={{ fontSize: 15, color: "var(--ink-60)", margin: "12px 0 8px" }}>
                All are NMC-verified, sleep-medicine-trained, and actively accepting new home sleep studies in <strong style={{ color: "var(--navy)" }}>{city}</strong>.
              </p>

              <div style={{ display: "grid", gap: 10, marginTop: 20 }}>
                {doctors.length === 0 && (
                  <div style={{ padding: 32, background: "white", border: "var(--border)", borderRadius: "var(--radius-lg)", textAlign: "center", color: "var(--grey-text)", fontSize: 14 }} data-testid="no-doctors-in-city">
                    No specialists listed in {city} yet. Please choose another city or email us — we&apos;re onboarding new doctors weekly.
                  </div>
                )}
                {doctors.map(d => (
                  <div
                    key={d.id}
                    onClick={() => setDoctorId(d.id)}
                    className={`sv-book-card ${doctorId === d.id ? "selected" : ""}`}
                    style={{ padding: 20, cursor: "pointer", margin: 0 }}
                    data-testid={`book-doctor-${d.slug}`}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <div className="sv-doc-avatar" style={{ width: 52, height: 52, fontSize: 14 }}>{d.avatar_initials}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 500 }}>{d.name}</div>
                        <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>{d.specialty}</div>
                        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                          <MapPin size={11} style={{ display: "inline", marginRight: 4 }} />
                          {d.hospital}
                        </div>
                      </div>
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, opacity: 0.7, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                        {d.experience_years}+ yrs
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
                <button onClick={() => setStep(1)} className="btn btn-ghost" data-testid="book-back-2">
                  <ArrowLeft size={14} /> Change city
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!doctorId}
                  className="btn btn-dark"
                  data-testid="book-next-2"
                >
                  Continue <ArrowRight size={14} />
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h1 data-testid="book-step-3-headline">Your details.</h1>
              <p style={{ fontSize: 15, color: "var(--ink-60)", margin: "12px 0 24px" }}>
                The doctor&apos;s team will call you within 24 hours on the number below to confirm the home visit. The technician will come to the address you enter.
              </p>

              <label className="sv-label">Full name</label>
              <input className="sv-input" value={patient.name} onChange={e => setPatient({ ...patient, name: e.target.value })} style={{ marginBottom: 14 }} placeholder="As on your ID" data-testid="booking-name" />

              <label className="sv-label">Email</label>
              <input className="sv-input" type="email" value={patient.email} onChange={e => setPatient({ ...patient, email: e.target.value })} style={{ marginBottom: 14 }} placeholder="you@example.com" data-testid="booking-email" />

              <label className="sv-label">Phone (WhatsApp)</label>
              <input className="sv-input" value={patient.phone} onChange={e => setPatient({ ...patient, phone: e.target.value })} style={{ marginBottom: 14 }} placeholder="+91 98XXX XXXXX" data-testid="booking-phone" />

              <label className="sv-label">Home address for the sleep study</label>
              <textarea
                className="sv-input"
                rows={3}
                value={patient.address}
                onChange={e => setPatient({ ...patient, address: e.target.value })}
                style={{ marginBottom: 14, resize: "vertical" }}
                placeholder="Flat / House no., Street, Locality, Landmark, Pincode"
                data-testid="booking-address"
              />

              <label className="sv-label">Preferred date for the home visit</label>
              <input
                className="sv-input"
                type="date"
                value={dateVal}
                onChange={e => setDateVal(e.target.value)}
                style={{ marginBottom: 14 }}
                min={new Date().toISOString().split('T')[0]}
                data-testid="booking-date"
              />
              <p style={{ fontSize: 12, color: "var(--grey-text)", margin: "-8px 0 16px", fontFamily: "var(--font-mono)", letterSpacing: "0.05em" }}>
                The technician will arrive in the evening and collect the equipment next morning. The doctor&apos;s team confirms the exact time when they call you.
              </p>

              <label className="sv-label">Notes for the doctor (optional)</label>
              <textarea
                className="sv-input"
                rows={3}
                value={patient.notes}
                onChange={e => setPatient({ ...patient, notes: e.target.value })}
                style={{ marginBottom: 14, resize: "vertical" }}
                placeholder="Existing conditions, medications, or anything the doctor should know"
                data-testid="booking-notes"
              />

              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 20, fontSize: 13, color: "var(--ink-60)" }}>
                <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 3 }} data-testid="booking-consent" />
                <span>I consent to sharing these details with Somveil and the selected specialist for the purpose of booking a home sleep study. DPDPA compliant.</span>
              </label>

              <div style={{ background: "var(--navy)", color: "white", padding: 24, borderRadius: "var(--radius-lg)", marginBottom: 20 }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--mint)" }}>Order summary</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
                  <div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 20 }}>Home Sleep Study</div>
                    <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 4 }}>
                      With {selectedDoctor?.name || "selected specialist"} · {dateVal || "date TBD"}
                    </div>
                  </div>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 500 }} data-testid="booking-price"><IndianRupee size={20} style={{ display: "inline", marginRight: -2, verticalAlign: "-2px" }} />{HOME_TEST_PRICE.toLocaleString()}</div>
                </div>
              </div>

              {err && <p className="sv-err">{err}</p>}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setStep(2)} className="btn btn-ghost" data-testid="book-back-3"><ArrowLeft size={14} /> Back</button>
                <button
                  onClick={submitBooking}
                  disabled={submitting || !consent}
                  className="btn btn-primary"
                  data-testid="make-payment-btn"
                >
                  {submitting ? "Submitting..." : `Make a payment · ₹${HOME_TEST_PRICE.toLocaleString()}`} <ArrowRight size={16} />
                </button>
              </div>
              <p style={{ fontSize: 11, color: "var(--ink-60)", marginTop: 14, lineHeight: 1.5, fontFamily: "var(--font-mono)", letterSpacing: "0.05em" }}>
                Payment gateway integration coming soon. For now your booking is captured and the doctor is notified; the team will confirm payment collection on the call.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
